import { AxiosResponse } from 'axios'
import { enqueueSnackbar } from 'notistack'
import {
    ExportUploadManagerParams,
    PaginatedUploadManagerResponse,
    Template,
} from '../components/Applications/UploadManager/interface'
import i18n from '../i18n'
import { downloadFileFromResponse } from '../utils/FileUtils'
import { axiosInstanceEntity } from './axiosInstance'
import { EntitySaveResponse } from './entityServices'
import { MRT_ColumnFiltersState } from 'material-react-table'

const handleUploadManagerError = async (err: any) => {
    let message = ''
    if (err?.response?.data instanceof Blob) {
        try {
            const text = await err.response.data.text()
            const data = JSON.parse(text)
            message = data?.displayMessage || data?.message
        } catch (e) {
            console.log(e)
        }
    } else {
        message =
            err?.response?.data?.displayMessage || err?.response?.data?.message
    }

    enqueueSnackbar(message || err?.message || 'error', {
        variant: 'error',
    })
}

export const fetchUploadManagerData = async (
    orgId: string,
    page = 0,
    size = 20,
    sortBy = 'createdAt',
    sortOrder = 'DESC',
    searchBy?: string,
    searchQuery?: string,
    filters?: MRT_ColumnFiltersState,
): Promise<PaginatedUploadManagerResponse> => {
    const response =
        await axiosInstanceEntity.post<PaginatedUploadManagerResponse>(
            'upload-manager',
            {
                orgId,
                page,
                size,
                sortBy,
                sortOrder,
                searchBy,
                searchQuery,
                filters,
            },
        )

    return response.data
}

export const exportUploadManagerToExcel = async (
    orgId: string,
    params: ExportUploadManagerParams,
    selectedColumns: string[],
): Promise<AxiosResponse<Blob>> => {
    return axiosInstanceEntity.post(
        '/upload-manager/download',
        {
            orgId,
            columns: selectedColumns,
            ...params,
        },
        {
            responseType: 'blob',
        },
    )
}

export const fetchUploadManagerTemplates = async (
    orgId: string,
): Promise<Template[]> => {
    const response = await axiosInstanceEntity.get<Template[]>(
        '/upload-manager/templates',
        {
            params: { orgId },
        },
    )
    return response.data
}

export const importBulkUploadFromExcel = async (
    file: File,
    loggedInOrgId: string,
    entityTemplateId: string,
    isProgram?: boolean,
    selectedProgramId?: string,
): Promise<EntitySaveResponse> => {
    const formData = new FormData()
    formData.append('file', file)

    let url = `/entity/bulk-upload/import?orgId=${loggedInOrgId}&entityTemplateId=${entityTemplateId}`
    if (isProgram && selectedProgramId) {
        url += `&program=true&programId=${selectedProgramId}`
    } else if (isProgram) {
        // Fallback if isProgram true but programId is not provided
        url += `&program=true`
    }

    return await axiosInstanceEntity
        .post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        .then((response: AxiosResponse): EntitySaveResponse => {
            enqueueSnackbar(
                i18n.t('uploadManager.bulkUpload.checkUploadManager'),
                {
                    variant: 'success',
                },
            )
            return { type: 'success' as const, payload: response.data }
        })
        .catch(async (err: any): Promise<EntitySaveResponse> => {
            await handleUploadManagerError(err)
            return { type: 'error' as const, payload: err.response }
        })
}

export const exportTemplateForBulkUpload = async (
    templateId: string,
    loggedInOrgId: string,
    isProgram?: boolean,
    selectedProgramId?: string,
): Promise<void> => {
    let url = `/entity/bulk-upload/export/template/v2`
    const queryParams = new URLSearchParams()
    queryParams.append('orgId', loggedInOrgId)
    if (isProgram && selectedProgramId) {
        queryParams.append('program', 'true')
        queryParams.append('programId', selectedProgramId)
    } else if (isProgram) {
        // Fallback
        queryParams.append('program', 'true')
    }
    url += `?${queryParams.toString()}`

    const postBody: { templateId: string; orgId?: string; programId?: string } =
        {
            templateId,
        }
    if (isProgram && selectedProgramId) {
        postBody.programId = selectedProgramId
    }
    postBody.orgId = loggedInOrgId

    await axiosInstanceEntity
        .post(url, postBody, {
            responseType: 'blob',
        })
        .then((response: AxiosResponse) => {
            downloadFileFromResponse(response, 'template.xlsx')
        })
        .catch(async (err: any) => {
            await handleUploadManagerError(err)
            return { type: 'error' as const, payload: err.response }
        })
}
